import { Box, Column, Row, Stack } from '@shipt/design-system-layouts'
import { Body, Headline } from '@shipt/design-system-typography'
import { Button } from '@shipt/design-system-buttons'
import { HDivider } from '@/features/shared/components/Dividers'
import { ProductDetailReviews } from '@/features/shop/components/ProductDetailRatingsAndReviews/ProductDetailReviews'
import { ProductDetailRatingsSummary } from '@/features/shop/components/ProductDetailRatingsAndReviews/ProductDetailRatingsSummary'
import { useInfiniteRatingsAndReviews } from '@/features/account/services/RatingsAndReviews/queries'
import { LoadingIndicator } from '@shipt/design-system-loading'
import { useEffect, useState, type ReactNode } from 'react'
import { trackElementClicked, trackElementViewed } from '@/features/shared/analytics/element'
import { screenSizes } from '@shipt/design-system-themes'
import styled from 'styled-components'

const ReviewsContainer = ({ children }: { children: ReactNode }) => (
  <Box
    marginBottom="xl"
    paddingBottom="sm"
    role="region"
    aria-labelledby="ratings-and-reviews-heading"
  >
    {children}
  </Box>
)

type Props = {
  productId: number
}

export const ProductDetailRatingsAndReviews = ({ productId }: Props) => {
  const [sortByValue, setSortByValue] = useState('newest')
  const {
    data: ratingsAndReviewsData,
    isLoading,
    fetchNextPage,
    isFetchingNextPage,
    isError,
  } = useInfiniteRatingsAndReviews(productId, sortByValue)
  const allReviews = ratingsAndReviewsData?.pages.flatMap((page) => page.reviews) || []
  const stats = ratingsAndReviewsData?.pages[0]?.stats
  const hasMoreReviews = Boolean(ratingsAndReviewsData?.pages.at(-1)?.pagination.next_cursor)
  const summary = ratingsAndReviewsData?.pages[0]?.summary

  useEffect(() => {
    trackElementViewed({
      display_sublocation: 'reviews',
      display_format: 'ratings_and_reviews',
    })
  }, [])

  if (isLoading) {
    return (
      <ReviewsContainer>
        <Row align="center" justify="center">
          <LoadingIndicator size="lg" />
        </Row>
      </ReviewsContainer>
    )
  }

  if (isError) {
    return (
      <ReviewsContainer>
        <HDivider />
        <Row align="center" justify="center" paddingBottom="xl">
          <Headline>Failed to load reviews. Please try again later.</Headline>
        </Row>
      </ReviewsContainer>
    )
  }

  return (
    <ReviewsContainer>
      <HDivider />
      <Row paddingTop="sm" paddingBottom="xl" as="header">
        <Headline>Ratings & reviews</Headline>
      </Row>
      {!!stats && (
        <Stack
          spacing="xl"
          direction={{ base: 'column', md: 'row' }}
          role="group"
          aria-label="Customer ratings and reviews summary"
        >
          <Column spacing="md">
            <ProductDetailRatingsSummary stats={stats} />
          </Column>
          {stats.total_reviews > 0 && (
            <ReviewsColumn spacing="xxl">
              {!!summary && (
                <Column spacing="md" paddingLeft={{ base: 0, lg: '3xl' }}>
                  <Headline size="md">What customers are saying</Headline>
                  <Body size="md">{summary}</Body>
                  <Body variant="secondary" size="sm">
                    AI-generated summary of reviews
                  </Body>
                </Column>
              )}
              <ProductDetailReviews
                reviews={allReviews}
                setSortByValue={setSortByValue}
                defaultValue={sortByValue}
              />
              {!!hasMoreReviews && (
                <Button
                  variant="secondary"
                  fullWidth
                  onClick={() => {
                    fetchNextPage()
                    trackElementClicked({
                      display_sublocation: 'reviews',
                      display_format: 'ratings_and_reviews',
                      content: 'see more reviews',
                      type: 'button',
                    })
                  }}
                  loading={isFetchingNextPage}
                  disabled={isFetchingNextPage}
                  aria-label="Load more customer reviews"
                >
                  See more reviews
                </Button>
              )}
            </ReviewsColumn>
          )}
        </Stack>
      )}
      <HDivider />
    </ReviewsContainer>
  )
}

const ReviewsColumn = styled(Column)`
  @media ${screenSizes.smDesktop} {
    width: 44.5rem;
  }
`
